const NESHAN_SDK_CSS =
  "https://static.neshan.org/sdk/leaflet/v1.9.4/neshan-sdk/v1.0.8/index.css";
const NESHAN_SDK_JS =
  "https://static.neshan.org/sdk/leaflet/v1.9.4/neshan-sdk/v1.0.8/index.js";

type NeshanLeafletModule = {
  Map: new (
    element: HTMLElement | string,
    options: Record<string, unknown>
  ) => NeshanLeafletMap;
  marker: (
    latlng: [number, number],
    options?: Record<string, unknown>
  ) => NeshanLeafletMarker;
  layerGroup: () => NeshanLeafletLayerGroup;
  polygon: (
    latlngs: [number, number][],
    options?: Record<string, unknown>
  ) => unknown;
  Icon: new (options: Record<string, unknown>) => unknown;
  Point: new (x: number, y: number) => unknown;
};

export type NeshanLeafletMap = {
  setView: (
    center: [number, number],
    zoom: number,
    options?: { animate?: boolean }
  ) => void;
  on: (event: string, handler: (event: { latlng: { lat: number; lng: number } }) => void) => void;
  off: (event: string, handler: (event: { latlng: { lat: number; lng: number } }) => void) => void;
  remove: () => void;
};

export type NeshanLeafletMarker = {
  addTo: (map: NeshanLeafletMap) => NeshanLeafletMarker;
  remove: () => void;
  on: (event: string, handler: () => void) => void;
  bindPopup: (content: string) => void;
  getPopup: () => { getElement: () => HTMLElement | null } | null;
  getLatLng: () => { lat: number; lng: number };
};

export type NeshanLeafletLayerGroup = {
  addTo: (map: NeshanLeafletMap) => NeshanLeafletLayerGroup;
  clearLayers: () => void;
  addLayer?: (layer: unknown) => NeshanLeafletLayerGroup;
};

declare global {
  interface Window {
    L?: NeshanLeafletModule;
  }
}

let loaderPromise: Promise<NeshanLeafletModule> | null = null;

function loadStylesheet(href: string): Promise<void> {
  const existing = document.querySelector(`link[href="${href}"]`);
  if (existing) {
    return Promise.resolve();
  }

  return new Promise((resolve, reject) => {
    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href = href;
    link.onload = () => resolve();
    link.onerror = () => reject(new Error("Failed to load Neshan map styles."));
    document.head.appendChild(link);
  });
}

function loadScript(src: string): Promise<void> {
  const existing = document.querySelector(`script[src="${src}"]`);
  if (existing) {
    return Promise.resolve();
  }

  return new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = src;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error("Failed to load Neshan map SDK."));
    document.body.appendChild(script);
  });
}

export async function loadNeshanLeaflet(): Promise<NeshanLeafletModule> {
  if (window.L?.Map) {
    return window.L;
  }

  if (!loaderPromise) {
    loaderPromise = (async () => {
      await loadStylesheet(NESHAN_SDK_CSS);
      await loadScript(NESHAN_SDK_JS);

      if (!window.L?.Map) {
        throw new Error("Neshan Leaflet SDK is unavailable.");
      }

      return window.L;
    })();
  }

  return loaderPromise;
}
